import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import {
  retrieveUserSavedFiltersApi,
  saveUserSavedFilterApi,
  updateUserSavedFilterApi,
  deleteUserSavedFilterApi,
} from "./api/UserSavedFilterSetApiService";

/**
 * Generic "My Filters" manager (backend persisted).
 *
 * Props:
 * - componentName: required; scope key on backend
 * - filterState: current filter object
 * - onApply(filtersObj): callback to apply saved filters to UI
 */
function MyFilters({ componentName, filterState, onApply }) {
  const [savedSets, setSavedSets] = useState([]);
  const [selectedId, setSelectedId] = useState(""); // keep as string to match MUI Select behavior
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [loadedOnce, setLoadedOnce] = useState(false);

  const loadSavedSets = useCallback(async () => {
    if (!componentName) return [];
    try {
      const response = await retrieveUserSavedFiltersApi(componentName);
      const serverData = Array.isArray(response?.data) ? response.data : [];

      const mapped = serverData.map((item) => {
        let parsed = {};
        try {
          parsed = item?.filterPayload ? JSON.parse(item.filterPayload) : {};
        } catch (e) {
          console.error("Failed to parse filterPayload for saved filter", e, item);
        }
        return {
          id: String(item.id), // normalize to string
          name: item.filterName,
          filters: parsed,
        };
      });

      setSavedSets(mapped);

      // if currently selected was deleted or missing, reset
      if (selectedId && !mapped.some((m) => m.id === selectedId)) {
        setSelectedId("");
      }

      return mapped;
    } catch (e) {
      console.error("Failed to load saved filters from backend", e);
      return [];
    }
  }, [componentName, selectedId]);

  const handleSelectOpen = async () => {
    if (!componentName) {
      console.error("MyFilters: componentName is required");
      return;
    }

    // Lazy-load from backend the first time user opens the dropdown,
    // then refresh on every open to stay in sync.
    await loadSavedSets();
    if (!loadedOnce) setLoadedOnce(true);
  };

  const handleApply = (id) => {
    const idStr = String(id);
    setSelectedId(idStr);

    const found = savedSets.find((s) => s.id === idStr);
    if (found && typeof onApply === "function") {
      onApply(found.filters || {});
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    try {
      await deleteUserSavedFilterApi(selectedId); // soft delete on backend
      setSelectedId("");
      await loadSavedSets();
    } catch (e) {
      console.error("Failed to delete saved filter", e);
    }
  };

  const handleOpenSave = () => {
    // If a set is selected, default the dialog name to it (rename/update style)
    const existing = selectedId ? savedSets.find((s) => s.id === selectedId) : null;
    setNewName(existing?.name || "");
    setSaveDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;

    if (!componentName) {
      console.error("MyFilters: componentName is required to save filters");
      return;
    }

    const payloadJson = JSON.stringify(filterState ?? {});

    try {
      const existing = selectedId ? savedSets.find((s) => s.id === selectedId) : null;

      if (existing) {
        // Update selected set (rename and/or replace payload)
        await updateUserSavedFilterApi(existing.id, trimmed, payloadJson);
        setSelectedId(existing.id);
      } else {
        // Create new set
        const res = await saveUserSavedFilterApi(componentName, trimmed, payloadJson);
        // If backend returns the created entity, we can select it directly
        const createdId = res?.data?.id != null ? String(res.data.id) : "";
        if (createdId) setSelectedId(createdId);
      }

      const mapped = await loadSavedSets();

      // If create response didn't include id, fall back to selecting by name (most recent list is first)
      if (!selectedId) {
        const sameName = mapped.find((m) => m.name === trimmed);
        setSelectedId(sameName ? sameName.id : "");
      }

      setSaveDialogOpen(false);
    } catch (e) {
      console.error("Failed to save filter set", e);
    }
  };

  const activeSummary = useMemo(() => {
    if (!filterState) return [];
    const entries = Object.entries(filterState);

    const isEmptyValue = (v) => {
      if (Array.isArray(v)) return v.length === 0;
      if (v === null || v === undefined) return true;
      if (typeof v === "string") return v.trim() === "";
      if (typeof v === "object") return Object.keys(v).length === 0; // helpful for ranges, etc.
      return false;
    };

    return entries.filter(([, v]) => !isEmptyValue(v)).slice(0, 20);
  }, [filterState]);

  const saveMode = selectedId ? "Update Selected Filter Set" : "Save New Filter Set";

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id={`${componentName}-my-filters-label`}>My Filters</InputLabel>
          <Select
            labelId={`${componentName}-my-filters-label`}
            label="My Filters"
            value={selectedId}
            onOpen={handleSelectOpen}
            onChange={(e) => handleApply(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {savedSets.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" size="small" onClick={handleOpenSave}>
          {selectedId ? "Update Saved Filter" : "Save Current Filters"}
        </Button>

        {selectedId && (
          <Button variant="text" size="small" color="error" onClick={handleDelete}>
            Delete
          </Button>
        )}
      </Stack>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{saveMode}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Summary of current filters
            </Typography>

            {activeSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No filters are currently set.
              </Typography>
            ) : (
              <Box
                sx={{
                  maxHeight: 220,
                  overflow: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                {activeSummary.map(([key, value]) => (
                  <Typography key={key} variant="body2">
                    <strong>{key}</strong>:{" "}
                    {Array.isArray(value) ? JSON.stringify(value) : typeof value === "object" ? JSON.stringify(value) : String(value)}
                  </Typography>
                ))}
                {activeSummary.length === 20 && (
                  <Typography variant="caption" color="text.secondary">
                    Showing first 20 non-empty fields.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Filter set name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            helperText="Name is required to save."
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!newName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyFilters;
